import React, { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, Plus, Edit3, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
  useListReceiptsQuery, useLazyListReceiptsQuery, useUpdateReceiptMutation, useDeleteReceiptMutation,
} from '../../api/billingApi';
import { selectHasRole } from '../auth/authSlice';
import { useInfiniteQuery } from '../../hooks/useInfiniteQuery';
import { fetchAllPages } from '../../utils/fetchAllPages';
import ExportButtons from '../../components/ExportButtons';
import DateRangePicker, { useDateRange } from '../../components/DateRangePicker';
import { AddNewReceiptModal } from './BillingPage';

const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const RECEIPT_COLUMNS = [
  { key: 'receiptNumber', label: 'Receipt No.' },
  { key: 'invoiceNumber', label: 'Invoice No.' },
  { key: 'patientName', label: 'Patient' },
  { key: 'paidAt', label: 'Timestamp' },
  { key: 'method', label: 'Method' },
  { key: 'amount', label: 'Amount (₹)' },
];

const RECEIPT_EXPORT_COLUMNS = [
  { key: 'receiptNumber', label: 'Receipt No.' },
  { key: 'invoiceNumber', label: 'Invoice No.' },
  { key: 'patientName', label: 'Patient' },
  { key: 'paidAt', label: 'Timestamp' },
  { key: 'method', label: 'Method' },
  { key: 'amount', label: 'Amount (₹)', align: 'right' },
];

/**
 * Billing → Receipts register: every Payment row across all invoices, flattened. Reads straight
 * off the Payments table via listReceipts (no separate store), so it can never drift from what
 * "Add New Receipt" / "Collect Payment" / "Clear Dues" actually wrote.
 */
export default function ReceiptsTab({ onOpenInvoice }) {
  const { preset, setPreset, customFrom, customTo, onCustomChange, range } = useDateRange('30d');
  const [sortBy, setSortBy] = useState('paidAt');
  const [sortDesc, setSortDesc] = useState(true); // newest receipt first, by default
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const isAdmin = useSelector(selectHasRole('Admin'));

  const filter = useMemo(
    () => ({ from: range.from || undefined, to: range.to || undefined, sortBy, sortDesc }),
    [range.from, range.to, sortBy, sortDesc],
  );
  const { items: rows, total, hasMore, isFetching, sentinelRef } =
    useInfiniteQuery(useListReceiptsQuery, filter, { pageSize: 25 });

  const [triggerReceipts] = useLazyListReceiptsQuery();
  const getExportRows = async () => {
    const all = await fetchAllPages(triggerReceipts, filter, 200);
    return all.map((r) => ({
      receiptNumber: r.transactionReference || '',
      invoiceNumber: r.invoiceNumber || '',
      patientName: r.patientName || '',
      paidAt: fmtDateTime(r.paidAt),
      method: r.method || '',
      amount: num(r.amount),
    }));
  };

  const [deleteReceipt] = useDeleteReceiptMutation();
  const deleteRow = async (r) => {
    if (!window.confirm(`Delete receipt ${r.transactionReference || ''} (${fmt(r.amount)}) from ${r.invoiceNumber || 'this invoice'}? This reduces the invoice's recorded payments and cannot be undone.`)) return;
    try {
      await deleteReceipt(r.id).unwrap();
      toast.success('Receipt deleted');
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not delete this receipt.');
    }
  };

  const setSort = (col) => {
    if (sortBy === col) setSortDesc((d) => !d);
    else { setSortBy(col); setSortDesc(true); }
  };

  const sortTh = (col, label, right = false) => (
    <th
      className="bill-sort-th"
      style={{ cursor: 'pointer', textAlign: right ? 'right' : 'left', whiteSpace: 'nowrap' }}
      onClick={() => setSort(col)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: right ? 'flex-end' : 'flex-start' }}>
        {label}
        {sortBy === col && (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
      </span>
    </th>
  );

  return (
    <>
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <DateRangePicker preset={preset} onPresetChange={setPreset} customFrom={customFrom} customTo={customTo} onCustomChange={onCustomChange} />
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            <Plus size={15} /> Add Receipt
          </button>
        )}
        <ExportButtons
          filename="receipts"
          title="Receipts"
          subtitle={`${total?.toLocaleString() ?? 0} receipts · exported ${new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`}
          columns={RECEIPT_EXPORT_COLUMNS}
          getRows={getExportRows}
          getSummary={(exportRows) => [
            { label: 'Total receipts', value: exportRows.length },
            { label: 'Total collected', value: fmt(exportRows.reduce((s, r) => s + num(r.amount), 0)) },
          ]}
          orientation="landscape"
        />
      </div>

      <div className="tbl-wrap tbl-elevated" style={{ marginTop: 10 }}>
        <table className="invoice-table">
          <thead>
            <tr>
              {sortTh('receiptNumber', 'Receipt No.')}
              {sortTh('invoiceNumber', 'Invoice No.')}
              {sortTh('patientName', 'Patient')}
              {sortTh('paidAt', 'Timestamp')}
              {sortTh('method', 'Method')}
              {sortTh('amount', 'Amount (₹)', true)}
              {isAdmin && <th style={{ width: 84 }} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="td-mono td-bold">{r.transactionReference || '—'}</td>
                <td className="td-mono">
                  {r.invoiceNumber ? (
                    <span
                      className="pay-link"
                      style={{ cursor: 'pointer' }}
                      onClick={() => onOpenInvoice?.(r.invoiceId)}
                      title="Open this invoice"
                    >
                      {r.invoiceNumber}
                    </span>
                  ) : '—'}
                </td>
                <td>{r.patientName || '—'}</td>
                <td className="td-muted">{fmtDateTime(r.paidAt)}</td>
                <td>{r.method || '—'}</td>
                <td className="td-bold" style={{ textAlign: 'right' }}>{fmt(r.amount)}</td>
                {isAdmin && (
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-icon-sm" title="Edit receipt" onClick={() => setEditTarget(r)}><Edit3 size={14} /></button>
                    <button className="btn-icon-sm" title="Delete receipt" onClick={() => deleteRow(r)}><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && !isFetching && (
              <tr><td colSpan={RECEIPT_COLUMNS.length + 1} className="td-muted" style={{ textAlign: 'center', padding: 24 }}>No receipts in this range.</td></tr>
            )}
          </tbody>
        </table>
        {isFetching && <div className="skeleton" style={{ height: 40 }} />}
        <div ref={sentinelRef} />
      </div>

      <div className="toolbar" style={{ marginTop: 8, justifyContent: 'center' }}>
        <span className="td-muted" style={{ fontSize: '0.8rem' }}>
          Showing {rows.length.toLocaleString()} of {total.toLocaleString()} receipt(s)
          {hasMore ? ' — scroll for more' : ''}
        </span>
      </div>

      {showAdd && (
        <AddNewReceiptModal onClose={() => setShowAdd(false)} onCreated={() => setShowAdd(false)} />
      )}
      {editTarget && (
        <EditReceiptModal receipt={editTarget} onClose={() => setEditTarget(null)} onSaved={() => setEditTarget(null)} />
      )}
    </>
  );
}

const EDIT_RECEIPT_METHODS = [
  { value: 'Cash', label: 'Cash' }, { value: 'Card', label: 'Card' }, { value: 'Upi', label: 'UPI' },
  { value: 'BankTransfer', label: 'Bank Transfer' }, { value: 'Online', label: 'Online' },
];

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const dateInputToIso = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
};

function EditReceiptModal({ receipt, onClose, onSaved }) {
  const [paidAtDate, setPaidAtDate] = useState(toDateInput(receipt.paidAt));
  const [amount, setAmount] = useState(String(receipt.amount ?? ''));
  const [method, setMethod] = useState(receipt.method || 'Cash');
  const [reference, setReference] = useState(receipt.transactionReference || '');
  const [notes, setNotes] = useState(receipt.notes || '');
  const [updateReceipt, { isLoading }] = useUpdateReceiptMutation();

  const submit = async (e) => {
    e.preventDefault();
    const amt = num(amount);
    if (amt <= 0) { toast.error('Enter a payment amount greater than zero.'); return; }
    if (!paidAtDate) { toast.error('Enter a payment date.'); return; }
    try {
      await updateReceipt({
        id: receipt.id,
        paidAt: dateInputToIso(paidAtDate),
        amount: amt,
        method,
        transactionReference: reference.trim() || null,
        notes: notes.trim() || null,
      }).unwrap();
      toast.success('Receipt updated');
      onSaved();
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not update this receipt.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <h2>Edit Receipt — {receipt.invoiceNumber || ''}</h2>
        </div>
        <div className="modal-body">
          <div className="td-muted" style={{ fontSize: '0.8rem', marginBottom: 10 }}>
            {receipt.patientName || 'Patient'} · Invoice <span className="td-mono">{receipt.invoiceNumber || '—'}</span>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Payment date *</label>
              <input className="form-control" type="date" value={paidAtDate} onChange={(e) => setPaidAtDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Amount (₹) *</label>
              <input className="form-control" type="number" min="0.01" step="any" inputMode="decimal"
                value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Method</label>
              <select className="form-control" value={method} onChange={(e) => setMethod(e.target.value)}>
                {EDIT_RECEIPT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Receipt No.</label>
              <input className="form-control" value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
